import type { BufferGeometry } from 'three';
import type { LinearRGB } from '../../core/color';
import type { Rng } from '../../core/rng';
import type { Vec2, Vec3 } from '../../geo/types';
import { createMeshBuilder, type VertexStyle } from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import { poissonInPolygon } from '../../geo/poisson';
import { minAreaOBB, pointInPolygon } from '../../geo/polygon';
import type { GroundPiece } from '../../site/types';
import { frustum, outwardPolygon } from './solidParts';

const TRUNK_RADIUS = 0.15;
const TRUNK_SIDES = 5;
const TRUNK_HEIGHT_MIN = 2;
const TRUNK_HEIGHT_MAX = 3.5;

const CROWN_RADIUS = 1.5;
const CROWN_DISPLACEMENT = 0.3;
const CROWN_SCALE_MIN = 0.8;
const CROWN_SCALE_MAX = 1.3;
const CROWN_VARIANTS = 3;
/* Below the 0.95 m inscribed radius of the most shrunken crown, so the trunk top stays inside it. */
const CROWN_CENTRE_ABOVE_TRUNK = 0.8;
/* Every template vertex of a crown is at most this far from its trunk axis, at the largest scale. */
const TREE_CROWN_REACH_M = (CROWN_RADIUS + CROWN_DISPLACEMENT) * CROWN_SCALE_MAX;
/* Each instance widens its trunk with its crown scale, so the widest trunk grows at the largest. */
const TREE_TRUNK_REACH_M = TRUNK_RADIUS * CROWN_SCALE_MAX;
/* The crown takes its colour from the instance; a neutral vertex colour leaves it unchanged. */
const NEUTRAL: LinearRGB = [1, 1, 1];

const ORCHARD_SPACING = 6;
const ORCHARD_JITTER = 0.8;
const GARDEN_POISSON_RADIUS = 7;
const GARDEN_KEEP_PROBABILITY = 0.4;
const CLOISTER_POISSON_RADIUS = 8;
const CLOISTER_KEEP_PROBABILITY = 0.25;
const YARD_POISSON_RADIUS = 10;
const YARD_KEEP_PROBABILITY = 0.2;

const GOLDEN = (1 + Math.sqrt(5)) / 2;
const ICOSAHEDRON_CORNERS: readonly Vec3[] = [
  [-1, GOLDEN, 0],
  [1, GOLDEN, 0],
  [-1, -GOLDEN, 0],
  [1, -GOLDEN, 0],
  [0, -1, GOLDEN],
  [0, 1, GOLDEN],
  [0, -1, -GOLDEN],
  [0, 1, -GOLDEN],
  [GOLDEN, 0, -1],
  [GOLDEN, 0, 1],
  [-GOLDEN, 0, -1],
  [-GOLDEN, 0, 1],
];
const ICOSAHEDRON_FACES: readonly (readonly [number, number, number])[] = [
  [0, 11, 5],
  [0, 5, 1],
  [0, 1, 7],
  [0, 7, 10],
  [0, 10, 11],
  [1, 5, 9],
  [5, 11, 4],
  [11, 10, 2],
  [10, 7, 6],
  [7, 1, 8],
  [3, 9, 4],
  [3, 4, 2],
  [3, 2, 6],
  [3, 6, 8],
  [3, 8, 9],
  [4, 9, 5],
  [2, 4, 11],
  [6, 2, 10],
  [8, 6, 7],
  [9, 8, 1],
];

export interface TreePlacement {
  readonly position: Vec2;
  readonly scale: number;
  readonly trunkHeight: number;
  readonly crownVariant: number;
}

export type IsBlocked = (p: Vec2) => boolean;

/* The placers read only the outline, so a city block serves as well as a ground piece. */
export type TreeGround = Pick<GroundPiece, 'polygon'>;

export function drawTreeShape(position: Vec2, rng: Rng): TreePlacement {
  return {
    position,
    scale: rng.range(CROWN_SCALE_MIN, CROWN_SCALE_MAX),
    trunkHeight: rng.range(TRUNK_HEIGHT_MIN, TRUNK_HEIGHT_MAX),
    crownVariant: rng.int(0, CROWN_VARIANTS - 1),
  };
}

/* A unit-height trunk: each instance scales it to its own trunk height and girth. */
export function buildTrunkTemplate(
  colour: LinearRGB,
  edgeId: number,
  seed: number
): BufferGeometry {
  const builder = createMeshBuilder();
  const style: VertexStyle = {
    color: colour,
    surface: SURFACE.wood,
    edgeId,
    seed,
    strokeAngle: Math.PI / 2,
    extent: [0, 0],
  };
  const shape = { sides: TRUNK_SIDES, radiusBottom: TRUNK_RADIUS, radiusTop: TRUNK_RADIUS };
  /* The top is hidden in the crown but closes the solid; the foot stands on the ground. */
  frustum(builder, style, { ...shape, y0: 0, y1: 1 }, { top: true, bottom: false });
  return builder.build();
}

/* A noise-deformed icosahedron centred on the origin (PLAN §5: detail 0 plus displacement). */
export function buildCrownTemplate(rng: Rng, edgeId: number, seed: number): BufferGeometry {
  const builder = createMeshBuilder();
  const style: VertexStyle = {
    color: NEUTRAL,
    surface: SURFACE.foliage,
    edgeId,
    seed,
    strokeAngle: 0,
    extent: [0, 0],
  };
  const corners = ICOSAHEDRON_CORNERS.map((c): Vec3 => {
    const radius = CROWN_RADIUS + rng.range(-CROWN_DISPLACEMENT, CROWN_DISPLACEMENT);
    const length = Math.hypot(c[0], c[1], c[2]);
    return [(c[0] / length) * radius, (c[1] / length) * radius, (c[2] / length) * radius];
  });
  for (const [a, b, c] of ICOSAHEDRON_FACES) {
    outwardPolygon(builder, style, [corners[a]!, corners[b]!, corners[c]!], [0, 0, 0]);
  }
  return builder.build();
}

export function placeOrchardTrees(
  piece: TreeGround,
  isBlocked: IsBlocked,
  rng: Rng
): readonly TreePlacement[] {
  const trees: TreePlacement[] = [];
  const obb = minAreaOBB(piece.polygon.outer);
  const [cosA, sinA] = obb.axisU;
  const numU = Math.floor((obb.halfU * 2) / ORCHARD_SPACING);
  const numV = Math.floor((obb.halfV * 2) / ORCHARD_SPACING);

  for (let u = 0; u <= numU; u++) {
    for (let v = 0; v <= numV; v++) {
      const localU = -obb.halfU + u * ORCHARD_SPACING + rng.range(-ORCHARD_JITTER, ORCHARD_JITTER);
      const localV = -obb.halfV + v * ORCHARD_SPACING + rng.range(-ORCHARD_JITTER, ORCHARD_JITTER);
      const pos: Vec2 = [
        obb.center[0] + localU * cosA - localV * sinA,
        obb.center[1] + localU * sinA + localV * cosA,
      ];
      if (pointInPolygon(pos, piece.polygon) && !isBlocked(pos)) {
        trees.push(drawTreeShape(pos, rng));
      }
    }
  }

  return trees;
}

function placeSparseTrees(
  piece: TreeGround,
  isBlocked: IsBlocked,
  rng: Rng,
  spacing: { readonly radius: number; readonly keep: number }
): readonly TreePlacement[] {
  return poissonInPolygon(piece.polygon, spacing.radius, rng)
    .filter(() => rng.chance(spacing.keep))
    .filter((pos) => !isBlocked(pos))
    .map((pos) => drawTreeShape(pos, rng));
}

export function placeGardenTrees(
  piece: TreeGround,
  isBlocked: IsBlocked,
  rng: Rng,
  keep = GARDEN_KEEP_PROBABILITY
) {
  return placeSparseTrees(piece, isBlocked, rng, {
    radius: GARDEN_POISSON_RADIUS,
    keep,
  });
}

export function placeCloisterTrees(piece: TreeGround, isBlocked: IsBlocked, rng: Rng) {
  return placeSparseTrees(piece, isBlocked, rng, {
    radius: CLOISTER_POISSON_RADIUS,
    keep: CLOISTER_KEEP_PROBABILITY,
  });
}

export function placeYardTrees(piece: TreeGround, isBlocked: IsBlocked, rng: Rng) {
  return placeSparseTrees(piece, isBlocked, rng, {
    radius: YARD_POISSON_RADIUS,
    keep: YARD_KEEP_PROBABILITY,
  });
}

export {
  TRUNK_HEIGHT_MIN,
  TRUNK_HEIGHT_MAX,
  CROWN_SCALE_MIN,
  CROWN_SCALE_MAX,
  CROWN_VARIANTS,
  CROWN_CENTRE_ABOVE_TRUNK,
  TREE_CROWN_REACH_M,
  TREE_TRUNK_REACH_M,
};
